import {
  Box,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { UiField, UiHorizontalAlign, UiSection, UiSectionLayout } from '../types/uiSchema';

export type InspectorSelection =
  | { kind: 'field'; fieldId: string }
  | { kind: 'section' }
  | { kind: null };

type Props = {
  selection: InspectorSelection;
  sectionStableId: string;
  /** Current UI field row for `${sectionStableId}.${fieldId}` */
  uiField: UiField | null;
  /** Section row from first matching tab (by section id) */
  uiSection: Partial<UiSection> | null;
  onPatchField: (patch: Partial<UiField>) => void;
  onPatchSection: (patch: Partial<UiSection>) => void;
};

const LAYOUTS: UiSectionLayout[] = ['single_column', 'two_column', 'grid', 'twelve_column'];
const ALIGNS: UiHorizontalAlign[] = ['left', 'center', 'right'];

export default function CanvasPropertiesPanel({
  selection,
  sectionStableId,
  uiField,
  uiSection,
  onPatchField,
  onPatchSection,
}: Props) {
  return (
    <Box sx={{ height: '100%', minHeight: 0, overflow: 'auto', pr: 0.5 }}>
      {!selection || selection.kind === null ? (
        <Typography variant="body2" color="text.secondary">
          Select a field on the artboard or choose &quot;Section&quot; below the frame header.
        </Typography>
      ) : null}

      {selection.kind === 'section' && (
        <Stack spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            Section <code>{sectionStableId || '—'}</code>
          </Typography>
          <TextField
            size="small"
            label="Title (UI override)"
            fullWidth
            value={uiSection?.title ?? ''}
            onChange={(e) => onPatchSection({ title: e.target.value })}
            helperText="Shown in runtime; falls back to form schema title if empty."
          />
          <TextField
            select
            size="small"
            label="Layout mode"
            fullWidth
            value={(uiSection?.layout as UiSectionLayout) || 'two_column'}
            onChange={(e) => onPatchSection({ layout: e.target.value as UiSectionLayout })}
          >
            {LAYOUTS.map((l) => (
              <MenuItem key={l} value={l}>{l}</MenuItem>
            ))}
          </TextField>
            <TextField
              select
              size="small"
              label="Density"
              fullWidth
              value={uiSection?.density || 'comfortable'}
              onChange={(e) => onPatchSection({ density: e.target.value as 'comfortable' | 'compact' })}
            >
              <MenuItem value="comfortable">Comfortable</MenuItem>
              <MenuItem value="compact">Compact</MenuItem>
            </TextField>
            <FormControlLabel
              control={(
                <Switch
                  size="small"
                  checked={Boolean(uiSection?.locked)}
                  onChange={(_, v) => onPatchSection({ locked: v })}
                />
              )}
              label="Lock section auto-layout"
            />
        </Stack>
      )}

      {selection.kind === 'field' && uiField && (
        <Stack spacing={1.5}>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {uiField.field_ref}
          </Typography>
          <TextField
            size="small"
            label="Placeholder"
            fullWidth
            value={uiField.placeholder ?? ''}
            onChange={(e) => onPatchField({ placeholder: e.target.value || undefined })}
          />
          <TextField
            size="small"
            label="Help text"
            fullWidth
            multiline
            minRows={2}
            value={uiField.help_text ?? ''}
            onChange={(e) => onPatchField({ help_text: e.target.value || undefined })}
          />
          <FormControlLabel
            control={(
              <Switch
                size="small"
                checked={Boolean(uiField.required)}
                onChange={(_, v) => onPatchField({ required: v })}
              />
            )}
            label="Required (UI override)"
          />
          <TextField
            select
            size="small"
            label="Width (legacy)"
            fullWidth
            value={uiField.width || 'half'}
            onChange={(e) => onPatchField({ width: e.target.value as 'full' | 'half', col_span: undefined })}
          >
            <MenuItem value="half">Half</MenuItem>
            <MenuItem value="full">Full</MenuItem>
          </TextField>
          <TextField
            size="small"
            label="Column span (1–12)"
            type="number"
            fullWidth
            inputProps={{ min: 1, max: 12 }}
            value={uiField.col_span ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '') {
                onPatchField({ col_span: undefined });
                return;
              }
              const n = Math.min(12, Math.max(1, parseInt(v, 10) || 1));
              onPatchField({ col_span: n });
            }}
            helperText="Overrides width when set. 12 = full row in 12-col layout."
          />
          <TextField
            select
            size="small"
            label="Alignment"
            fullWidth
            value={uiField.horizontal_align || 'left'}
            onChange={(e) => onPatchField({ horizontal_align: e.target.value as UiHorizontalAlign })}
          >
            {ALIGNS.map((a) => (
              <MenuItem key={a} value={a}>{a}</MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={(
              <Switch
                size="small"
                checked={Boolean(uiField.locked)}
                onChange={(_, v) => onPatchField({ locked: v })}
              />
            )}
            label="Lock field auto-layout"
          />
        </Stack>
      )}
    </Box>
  );
}
